/**
 * The one photo picker, used everywhere a person or an event gets a picture.
 *
 * Every surface that collected an image had grown its own version — signup read
 * a file and previewed it with no way to frame it, the admin drawers had a bare
 * "Upload photo" label, and the CRM's ImageUploader had a real crop step nobody
 * else could reach. Same job, four behaviours, and only one of them let you
 * decide which part of the photo people would actually see.
 *
 * What this does, in the shape people already know from every social product:
 * drop a file or click to choose, then drag to reposition and zoom until the
 * subject sits where you want inside the frame you will actually be shown in —
 * a circle for a person, a rectangle for event art — then confirm.
 *
 * IMPORTANT — this component does not upload anything.
 *
 * It hands back a cropped data URL and nothing else. Every caller keeps its own
 * existing mutation, so photos continue through `storeMemberPhoto` / `storagePut`
 * and the server normalizes them to the canonical WebP representation before
 * storage. Changing the framing UI must not change where bytes live, and it does
 * not: the persistence path is shared.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Camera, Check, Trash2, UploadCloud, X, ZoomIn } from "lucide-react";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@shared/imageUploadLimits";
import { cropImageToDataUrl } from "@/lib/cropImage";

/**
 * What the picker accepts.
 *
 * Matches the server's magic-byte check in server/memberPhoto.ts. SVG is absent
 * on purpose: it is markup rather than pixels and can carry script, so serving
 * one back from our own origin would be an XSS vector against the session that
 * uploaded it.
 */
export const PHOTO_PICKER_TYPES = ["image/png", "image/jpeg", "image/webp"];

export type PhotoPickerShape = "circle" | "rectangle";

/** Output sizes. Square for people; wider for event art. */
const OUTPUT = {
  circle: { width: 640, height: 640, aspect: 1 },
  rectangle: { width: 1600, height: 900, aspect: 16 / 9 },
} as const;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("That image could not be read. Try another file."));
    };
    reader.onerror = () => reject(new Error("That image could not be read. Try another file."));
    reader.readAsDataURL(file);
  });
}

export default function PhotoPicker({
  value,
  onChange,
  shape = "circle",
  label = "photo",
  disabled = false,
  size = 96,
}: {
  /** The current image, or null. Shown as the preview when set. */
  value: string | null;
  /** Receives the cropped data URL, or null when the photo is removed. */
  onChange: (dataUrl: string | null) => void;
  shape?: PhotoPickerShape;
  /** Names the thing in button labels: "photo", "portrait", "event image". */
  label?: string;
  disabled?: boolean;
  /** Diameter (circle) or height (rectangle) of the resting preview, in px. */
  size?: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The freshly chosen image being framed. Null when the editor is closed. */
  const [editing, setEditing] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);

  const output = OUTPUT[shape];
  const radius = shape === "circle" ? "50%" : ".75rem";

  const accept = useCallback(async (file: File) => {
    setError(null);
    if (!PHOTO_PICKER_TYPES.includes(file.type)) {
      setError("Choose a PNG, JPEG, or WebP image.");
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setError(`That image is larger than ${MAX_IMAGE_UPLOAD_LABEL}. Choose a smaller one.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      // Straight into the editor rather than accepting the file as-is: framing is
      // the point, and a picker that silently takes the whole photo is the
      // behaviour this component replaces.
      setEditing(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : "That image could not be read.");
    }
  }, []);

  /** Escape leaves the editor without keeping the change. */
  useEffect(() => {
    if (!editing) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setEditing(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing]);

  async function confirmCrop() {
    if (!editing || !pixelCrop) return;
    setWorking(true);
    try {
      const cropped = await cropImageToDataUrl({
        src: editing,
        crop: pixelCrop,
        targetWidth: output.width,
        targetHeight: output.height,
      });
      onChange(cropped);
      setEditing(null);
    } catch (cropError) {
      setError(cropError instanceof Error ? cropError.message : "That image could not be prepared.");
    } finally {
      setWorking(false);
    }
  }

  const frameWidth = shape === "circle" ? size : Math.round(size * (16 / 9));

  return (
    <div style={{ display: "grid", gap: ".6rem", justifyItems: "start" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".9rem", flexWrap: "wrap" }}>
        {/*
          The resting control is the preview itself: click the picture to change
          the picture. A separate "browse" button beside a dead thumbnail is the
          pattern this replaces.
        */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          onDragOver={event => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setDragging(false);
            const file = event.dataTransfer.files?.[0];
            if (file) void accept(file);
          }}
          aria-label={value ? `Change ${label}` : `Add a ${label}`}
          style={{
            position: "relative",
            width: frameWidth,
            height: size,
            flexShrink: 0,
            padding: 0,
            borderRadius: radius,
            border: dragging ? "2px dashed #7DEAFF" : "1px solid rgba(255,255,255,.18)",
            background: value ? "#0D1220" : "linear-gradient(145deg, rgba(233,30,140,.18), rgba(124,58,237,.16))",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            cursor: disabled ? "default" : "pointer",
            color: "#E9D5FF",
          }}
        >
          {value
            ? <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <Camera size={Math.max(18, Math.round(size / 4))} aria-hidden="true" />}
        </button>

        <div style={{ display: "grid", gap: ".35rem" }}>
          <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              style={secondaryButton}
            >
              <UploadCloud size={13} aria-hidden="true" /> {value ? `Change ${label}` : `Upload ${label}`}
            </button>
            {value && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => { onChange(null); setError(null); }}
                style={{ ...secondaryButton, color: "#FCA5A5", borderColor: "rgba(251,113,133,.4)" }}
              >
                <Trash2 size={13} aria-hidden="true" /> Remove
              </button>
            )}
          </div>
          <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            Drag an image here or click to choose. PNG, JPEG or WebP up to {MAX_IMAGE_UPLOAD_LABEL}.
          </span>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_PICKER_TYPES.join(",")}
        hidden
        onChange={event => {
          const file = event.target.files?.[0];
          // Cleared so choosing the same file twice in a row still fires.
          event.target.value = "";
          if (file) void accept(file);
        }}
      />

      {error && (
        <p role="alert" style={{ margin: 0, color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
          {error}
        </p>
      )}

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Position your ${label}`}
          style={{ position: "fixed", inset: 0, zIndex: 4000, display: "grid", placeItems: "center", padding: "1rem", background: "rgba(3,6,17,.88)", backdropFilter: "blur(10px)" }}
        >
          <div style={{ width: "min(460px, 100%)", borderRadius: "1rem", border: "1px solid rgba(255,255,255,.12)", background: "#0D1220", padding: "1.1rem", display: "grid", gap: ".9rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".5rem" }}>
              <h2 style={{ margin: 0, color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1rem", fontWeight: 750 }}>
                Position your {label}
              </h2>
              <button type="button" aria-label="Cancel" onClick={() => setEditing(null)} style={{ ...secondaryButton, padding: ".3rem .45rem" }}>
                <X size={14} aria-hidden="true" />
              </button>
            </div>

            {/*
              The crop window wears the shape the image will be shown in, so what
              you frame is what appears — a square editor for a circular avatar
              makes people cut their own head off.
            */}
            <div style={{ position: "relative", width: "100%", height: 280, borderRadius: ".75rem", overflow: "hidden", background: "#05070F" }}>
              <Cropper
                image={editing}
                crop={crop}
                zoom={zoom}
                aspect={output.aspect}
                cropShape={shape === "circle" ? "round" : "rect"}
                showGrid={shape !== "circle"}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPixels) => setPixelCrop(areaPixels)}
              />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: ".6rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              <ZoomIn size={14} aria-hidden="true" />
              <span className="sr-only">Zoom</span>
              <input
                type="range"
                aria-label="Zoom"
                min={1}
                max={4}
                step={0.01}
                value={zoom}
                onChange={event => setZoom(Number(event.target.value))}
                style={{ flex: 1, accentColor: "#E91E8C" }}
              />
            </label>

            <p style={{ margin: 0, color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              Drag the image to reposition it, and use the slider to zoom.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: ".5rem" }}>
              <button type="button" onClick={() => setEditing(null)} disabled={working} style={secondaryButton}>Cancel</button>
              <button
                type="button"
                onClick={() => void confirmCrop()}
                disabled={working || !pixelCrop}
                style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", minHeight: 38, padding: "0 .9rem", borderRadius: ".6rem", border: 0, background: "linear-gradient(135deg,#E91E8C,#B91C72)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, cursor: working ? "wait" : "pointer" }}
              >
                <Check size={14} aria-hidden="true" /> {working ? "Preparing…" : "Use this photo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const secondaryButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: ".35rem",
  minHeight: 34,
  padding: "0 .7rem",
  borderRadius: ".55rem",
  border: "1px solid rgba(255,255,255,.16)",
  background: "rgba(255,255,255,.04)",
  color: "#D5DDEC",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  fontWeight: 700,
  cursor: "pointer",
} as const;
